import type {
  SdkApprovalResponse,
  SdkApprovalResult,
} from "@magnus/contract/approvals";
import type { SdkArtifactPickerItem } from "@magnus/contract/artifacts";
import type {
  SdkFavoriteRoomsInput,
  SdkFavoriteRoomsResponse,
  SdkRoomLookupQuery,
  SdkRoomsResponse,
} from "@magnus/contract/calendar";
import type {
  SdkChatListPage,
  SdkChatMessages,
  SdkChatRenameInput,
  SdkChatSummary,
} from "@magnus/contract/chats";
import type {
  SdkFeedbackMap,
  SdkFeedbackSubmitInput,
} from "@magnus/contract/feedback";
import type { MagnusUIMessage } from "@magnus/contract/messages";
import type { SdkModelList } from "@magnus/contract/models";
import type { SdkSkillPickerItem } from "@magnus/contract/skills";
import type { ChatCreateInput, CreatedChat } from "@magnus/contract/turn";
import type {
  SdkUploadCreateInput,
  SdkUploadFinalized,
  SdkUploadMinted,
  SdkUploadStatus,
} from "@magnus/contract/uploads";
import type {
  SdkLiveSessionAnswer,
  SdkLiveSessionRequest,
  SdkLiveVoiceAvailability,
  SdkVoiceSessionSaved,
  SdkVoiceSessionSaveInput,
  SdkVoiceToken,
  SdkVoiceTokenGrant,
  SdkVoiceTtsRequest,
} from "@magnus/contract/voice";
import {
  createTRPCClient,
  httpBatchLink,
  httpLink,
  isNonJsonSerializable,
  splitLink,
} from "@trpc/client";
import type { AnyTRPCRouter } from "@trpc/server";
import superjson from "superjson";
import type { AuthStrategy } from "./auth/types";
import { createSdkRestSurface, type SdkRestResult } from "./rest";
import { createMagnusFetch } from "./transport/fetch";

export interface MagnusConfig {
  /** How requests authenticate to Magnus. See `@bg-bgi/magnus-sdk/auth`. */
  auth: AuthStrategy;
  /**
   * Origin of the Magnus backend. Omit (or pass "") for the web app, which
   * hosts the backend locally — requests then go to relative `/api/*`.
   */
  baseUrl?: string;
  /** Underlying fetch. Defaults to the global. */
  fetch?: typeof globalThis.fetch;
}

export interface MagnusClient<TRouter extends AnyTRPCRouter = AnyTRPCRouter> {
  /**
   * Imperative tRPC client over the Magnus API. Pass your router type to
   * `createMagnus<TRouter>()` for full procedure typing (in-repo consumers
   * pass `AppRouter`); external consumers get an untyped-but-callable client
   * until a curated public router contract ships.
   */
  readonly api: ReturnType<typeof createTRPCClient<TRouter>>;
  /** `@`-mention artifact picker items over `/api/sdk/v1/artifacts`. */
  readonly artifacts: {
    listPickerItems(): Promise<SdkArtifactPickerItem[]>;
  };
  /** The configured auth strategy. */
  readonly auth: AuthStrategy;
  /** Resolved base URL ("" when relative). */
  readonly baseUrl: string;
  /**
   * Meeting-booking lookups for the in-chat booking dialog: free rooms for a
   * window and the caller's starred rooms. Same auth-applied fetch as the
   * rest of the client, so an embedded host (bearer token, remote `baseUrl`)
   * reaches them without a same-origin session cookie.
   */
  readonly calendar: {
    getFavoriteRooms(options?: {
      signal?: AbortSignal;
    }): Promise<SdkFavoriteRoomsResponse>;
    listRooms(
      query: SdkRoomLookupQuery,
      options?: { signal?: AbortSignal }
    ): Promise<SdkRoomsResponse>;
    setFavoriteRooms(
      input: SdkFavoriteRoomsInput,
      options?: { signal?: AbortSignal }
    ): Promise<SdkFavoriteRoomsResponse>;
  };
  /**
   * Typed chat operations, usable without a router type. Implemented over
   * the tRPC client; shapes come from the authored contract
   * (`@magnus/contract/turn`).
   */
  readonly chats: {
    /** Create a chat to drive with `useMagnus`. Typed via the authored
     *  contract (`CreatedChat`), implemented over the tRPC client. */
    create(input?: ChatCreateInput): Promise<CreatedChat>;
    /** List the caller's chats, most-recent first. Cursor-paginated.
     *  Implemented over the versioned REST surface (`/api/sdk/v1`). */
    list(input?: { limit?: number; cursor?: string }): Promise<SdkChatListPage>;
    /** One chat plus its persisted messages (history hydration). */
    get(chatId: string): Promise<SdkChatMessages>;
    /** Rename a chat; resolves the updated summary (ISO dates). */
    rename(chatId: string, input: SdkChatRenameInput): Promise<SdkChatSummary>;
    /** Hard-delete a chat (204, no body). */
    delete(chatId: string): Promise<void>;
    /** Stop the in-flight run, persisting the partial transcript so a
     *  reload shows it. Wire to `useMagnus`'s `onStop` option. */
    stop(chatId: string, partialMessages?: MagnusUIMessage[]): Promise<void>;
    /** Save a finished live call's recap (plan 042); idempotent on
     *  `sessionId`. Resolves the response rather than throwing on a
     *  non-2xx so callers can retry transient statuses. */
    saveVoiceSession(
      chatId: string,
      input: SdkVoiceSessionSaveInput,
      options?: { keepalive?: boolean }
    ): Promise<SdkRestResult<SdkVoiceSessionSaved>>;
  };
  /** Typed feedback operations over `/api/sdk/v1` — the caller's thumbs
   *  state per chat, and submitting a vote on a message. */
  readonly feedback: {
    get(chatId: string): Promise<SdkFeedbackMap>;
    submit(chatId: string, input: SdkFeedbackSubmitInput): Promise<void>;
  };
  /** Auth-applied fetch — used by the chat transport and tool-result poster. */
  readonly fetch: typeof globalThis.fetch;
  /** The model catalog AS DATA (plan 016): the server flag-filters the
   *  private catalog for the caller; ids are plain strings. */
  readonly models: {
    list(): Promise<SdkModelList>;
  };
  /** Skill slash-picker items over `/api/sdk/v1/skills`. */
  readonly skills: {
    listPickerItems(): Promise<SdkSkillPickerItem[]>;
  };
  /** Resolve a HITL approval pause over `/api/sdk/v1/tool-approvals`
   *  (ADR-091). The turn streams a `data-approval-request` part; the
   *  user's decision posts here and the paused turn resumes. */
  readonly toolApprovals: {
    submit(input: SdkApprovalResponse): Promise<SdkApprovalResult>;
  };
  /** Direct-to-blob uploads (plans/002) over `/api/sdk/v1/uploads`: mint
   *  the SAS, PUT bytes straight to storage, finalize. */
  readonly uploads: {
    create(input: SdkUploadCreateInput): Promise<SdkUploadMinted>;
    /** Finalize a direct upload; resolves the finalized row (url,
     *  storageKey, processingStatus). `signal` aborts the request. */
    finalize(
      uploadId: string,
      options?: { signal?: AbortSignal }
    ): Promise<SdkUploadFinalized>;
    get(uploadId: string): Promise<SdkUploadStatus>;
    delete(uploadId: string): Promise<void>;
    /** Re-run processing for a failed upload (or failed rich pass).
     *  Optional so a hand-rolled or older client still satisfies the type;
     *  without it the uploads source offers no retry on a failed sent tile.
     *  The client `createMagnusClient` returns always implements it. */
    retryProcessing?(uploadId: string): Promise<void>;
  };
  /** Voice (plan 027) over `/api/sdk/v1/voice`: mint least-privilege
   *  Cartesia tokens for direct WS/batch calls, plus the HTTP TTS
   *  fallback; and live calls (plan 042): availability, the WebRTC
   *  session mint, and hangup. */
  readonly voice: {
    endLiveSession(
      callSessionId: string,
      options?: { keepalive?: boolean }
    ): Promise<void>;
    liveAvailability(): Promise<SdkLiveVoiceAvailability>;
    liveSession(
      input: SdkLiveSessionRequest,
      options?: { signal?: AbortSignal }
    ): Promise<SdkRestResult<SdkLiveSessionAnswer>>;
    mintToken(grant: SdkVoiceTokenGrant): Promise<SdkVoiceToken>;
    tts(input: SdkVoiceTtsRequest): Promise<ArrayBuffer>;
  };
}

/**
 * Create a Magnus client: the single handle the React adapter and your
 * own code use to talk to Magnus. Wires auth into a shared `fetch`, then
 * exposes a typed tRPC client (`magnus.api.chat.list.query()`).
 */
export function createMagnus<TRouter extends AnyTRPCRouter = AnyTRPCRouter>(
  config: MagnusConfig
): MagnusClient<TRouter> {
  const baseUrl = config.baseUrl ?? "";
  const fetch = createMagnusFetch({
    baseUrl,
    auth: config.auth,
    fetch: config.fetch,
  });
  const trpcUrl = `${baseUrl}/api/trpc`;

  // tRPC's `fetch` option is typed as a slightly narrower `FetchEsque`;
  // the global fetch is structurally compatible.
  const trpcFetch = fetch as unknown as typeof globalThis.fetch;

  // Built against `AnyTRPCRouter` (not the caller's `TRouter`): the link
  // options' `transformer` field is conditionally typed on the router's
  // `$types.transformer` flag, which an unresolved generic can't satisfy.
  // Runtime behavior is identical; the cast below restores the caller's
  // procedure typing.
  const api = createTRPCClient<AnyTRPCRouter>({
    links: [
      splitLink({
        condition: (op) => isNonJsonSerializable(op.input),
        // FormData / multipart uploads can't be batched or JSON-encoded.
        true: httpLink({
          url: trpcUrl,
          transformer: superjson,
          fetch: trpcFetch,
        }),
        false: httpBatchLink({
          url: trpcUrl,
          transformer: superjson,
          fetch: trpcFetch,
        }),
      }),
    ],
  }) as MagnusClient<TRouter>["api"];

  // Authored-contract cast: the server's `chat.create` returns the created
  // chat row; the SDK promises only the authored `CreatedChat` subset. Same
  // trust model as the rest of `@magnus/contract` (drift is the server's
  // contract to keep, checked in-repo). The `AnyTRPCRouter` proxy type
  // can't be traversed statically, so the cast names the one procedure.
  const untypedApi = api as unknown as {
    chat: {
      create: { mutate: (input?: ChatCreateInput) => Promise<unknown> };
      stop: {
        mutate: (input: {
          chatId: string;
          partialMessages?: MagnusUIMessage[];
        }) => Promise<unknown>;
      };
    };
  };
  // REST-surface methods share the auth-applied fetch — no second wrapper.
  const rest = createSdkRestSurface({ baseUrl, fetch });
  const chats: MagnusClient["chats"] = {
    create: (input?: ChatCreateInput) =>
      untypedApi.chat.create.mutate(input) as Promise<CreatedChat>,
    list: rest.chats.list,
    get: rest.chats.get,
    rename: rest.chats.rename,
    delete: rest.chats.delete,
    saveVoiceSession: rest.chats.saveVoiceSession,
    stop: async (chatId, partialMessages) => {
      await untypedApi.chat.stop.mutate({ chatId, partialMessages });
    },
  };

  return {
    baseUrl,
    auth: config.auth,
    fetch,
    api,
    calendar: rest.calendar,
    chats,
    feedback: rest.feedback,
    toolApprovals: rest.toolApprovals,
    models: rest.models,
    skills: rest.skills,
    artifacts: rest.artifacts,
    uploads: rest.uploads,
    voice: rest.voice,
  };
}
